import React, { useEffect, useState } from 'react';
import { Link, Navigate, useLocation } from 'react-router-dom';
import { Car, ExternalLink, Home, LayoutDashboard, LogOut, Moon, Sun } from 'lucide-react';
import AutomobileLoginPage from './AutomobileLoginPage';
import AutomobileManager from './AutomobileManager';
import { useAuth } from '../../../contexts/AuthContext';

interface Props {
  darkMode: boolean;
  onToggleDarkMode: () => void;
}

const AutomobileAdminShell: React.FC<Props> = ({ darkMode, onToggleDarkMode }) => {
  const { user, signOut } = useAuth();
  const { pathname } = useLocation();
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  useEffect(() => {
    if (darkMode) document.documentElement.classList.add('dark');
    else document.documentElement.classList.remove('dark');
  }, [darkMode]);

  const handleSignOut = async () => {
    try {
      setIsLoggingOut(true);
      await signOut();
    } catch (e) {
      console.error(e);
    } finally {
      setIsLoggingOut(false);
    }
  };

  if (isLoggingOut) {
    return <Navigate to="/otomobil/login" replace />;
  }

  if (pathname === '/otomobil/login') {
    return <AutomobileLoginPage />;
  }

  if (!user) {
    return <Navigate to="/otomobil/login" replace state={{ from: pathname }} />;
  }

  const navLink = (to: string, label: string, icon: React.ReactNode) => (
    <Link
      to={to}
      className={`flex items-center gap-2 px-4 py-2 transition-colors ${
        pathname === to || (to === '/otomobil/admin' && pathname.startsWith('/otomobil/admin'))
          ? 'bg-indigo-50 dark:bg-gray-700 text-indigo-600 dark:text-indigo-300'
          : 'text-gray-700 dark:text-gray-300 hover:bg-indigo-50 dark:hover:bg-gray-700 hover:text-indigo-600 dark:hover:text-indigo-400'
      }`}
    >
      {icon}
      {label}
    </Link>
  );

  return (
    <div className="min-h-screen bg-gray-100 dark:bg-gray-900 transition-colors">
      <div className="flex">
        <aside className="w-64 bg-white dark:bg-gray-800 shadow-sm h-screen fixed">
          <div className="p-4 flex items-start justify-between gap-2">
            <div>
              <h1 className="text-lg font-semibold text-indigo-600 dark:text-indigo-300 flex items-center gap-2">
                <Car size={22} />
                Otomobil
              </h1>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Yönetim paneli</p>
            </div>
            <button
              type="button"
              onClick={onToggleDarkMode}
              className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
              aria-label="Tema"
            >
              {darkMode ? <Sun className="text-amber-500" size={20} /> : <Moon className="text-indigo-600" size={20} />}
            </button>
          </div>
          <nav className="mt-2">
            {navLink('/otomobil/admin', 'Markalar & Modeller', <LayoutDashboard size={20} />)}
            <a
              href="/otomobil"
              target="_blank"
              rel="noreferrer"
              className="flex items-center gap-2 px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-indigo-50 dark:hover:bg-gray-700"
            >
              <ExternalLink size={20} />
              Kataloğu görüntüle
            </a>
            <Link
              to="/"
              className="flex items-center gap-2 px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-indigo-50 dark:hover:bg-gray-700"
            >
              <Home size={20} />
              Forum sitesi
            </Link>
            <button
              type="button"
              onClick={handleSignOut}
              className="w-full flex items-center gap-2 px-4 py-2 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-gray-700"
            >
              <LogOut size={20} />
              Çıkış
            </button>
          </nav>
        </aside>

        <main className="ml-64 flex-1 p-8">
          <AutomobileManager />
        </main>
      </div>
    </div>
  );
};

export default AutomobileAdminShell;
